import type { components, FolioChatRequest } from '@contract-analyzer/api-client';
import { z } from 'zod';

type ApiPageContextInput = components['schemas']['PageContext-Input'];
/** The results Sean's grid tools read for a message; see docs/architecture/sean-grounded-results.md. */
export type FolioResultScope = NonNullable<FolioChatRequest['result_scope']>;
type ApiPageContextOutput = components['schemas']['PageContext-Output'];
type ApiPageContextEntitiesInput = NonNullable<ApiPageContextInput['entities']>;
// The API serializes stored entities as an open string map, so a stored
// context is the one place an entity key is not known in advance.
type StoredPageContextEntities = NonNullable<ApiPageContextOutput['entities']>;
type ApiPageContextTransitionOutput = NonNullable<ApiPageContextOutput['context_transition']>;
type DefinedString<Value> = Exclude<Value, null | undefined>;
type MutablePageContextEntities = {
  -readonly [Key in keyof PageContextEntities]?: PageContextEntities[Key];
};

/**
 * Every page name the API knows, and whether the web may send it yet.
 *
 * Keyed by the API's own list, so a name the API adds does not compile until
 * someone decides here. A new name starts `awaiting-release`: API replicas from
 * the previous release reject it (422) and cannot read a conversation that
 * stored it, which a rolling deploy or a rollback puts in front of users. It
 * becomes `sendable` in a later pull request, once a release that accepts it
 * runs everywhere. See "Adding a page name" in docs/architecture/folio-howto-design.md.
 *
 * `useFolioPageContext` round-trips each registration through
 * `parsePageContextKey`, so a name missing here would also turn a registered
 * page into an unregistered route, which hides Sean.
 */
const PAGE_NAMES = {
  'unregistered-route': 'sendable',
  'quick-answer': 'sendable',
  dashboard: 'sendable',
  admin: 'sendable',
  members: 'sendable',
  'project-detail': 'sendable',
  templates: 'sendable',
  'template-detail': 'sendable',
  'template-editor': 'sendable',
  'run-detail': 'sendable',
  'run-results': 'sendable',
  'analysis-workspace': 'sendable',
  analyses: 'sendable',
  evaluations: 'sendable',
  'evaluation-detail': 'sendable',
  'evaluation-compare': 'sendable',
  'ground-truth': 'sendable',
  'ground-truth-annotate': 'sendable',
  documents: 'sendable',
  'document-detail': 'sendable',
  'document-upload': 'sendable',
  'control-plane': 'sendable',
  // Accepted by the API from the release after 2.3.0; sendable after that ships (#951).
  settings: 'awaiting-release',
} as const satisfies Record<PageName, 'sendable' | 'awaiting-release'>;

type PageNameRollout = typeof PAGE_NAMES;

type ChatRequestFieldRollout = 'sendable' | 'awaiting-release';

/**
 * Optional chat request fields and whether the web may send them yet. Replicas of
 * the previous release reject an unknown key (422), so a new field waits for a
 * release that accepts it, as a page name does.
 */
const CHAT_REQUEST_FIELDS: Readonly<Record<'result_scope', ChatRequestFieldRollout>> = {
  // Accepted by the API from the release after this field was added; sendable after that ships.
  result_scope: 'awaiting-release',
};

export function isChatRequestFieldSendable(field: keyof typeof CHAT_REQUEST_FIELDS): boolean {
  return CHAT_REQUEST_FIELDS[field] === 'sendable';
}

/** A page name every API replica already accepts, so the web may send it. */
export type SendablePageName = {
  [Name in keyof PageNameRollout]: PageNameRollout[Name] extends 'sendable' ? Name : never;
}[keyof PageNameRollout];

const PAGE_CAPABILITIES = {
  portfolio_overview: true,
  setup_guidance: true,
  project_creation: true,
  project_overview: true,
  project_configuration: true,
  member_management: true,
  tenant_administration: true,
  run_overview: true,
  evaluation_overview: true,
  template_navigation: true,
  template_catalog: true,
  template_creation: true,
  template_review: true,
  template_authoring: true,
  template_iteration: true,
  template_schema_editing: true,
  run_monitoring: true,
  evaluation_launch: true,
  result_review: true,
  ground_truth_review: true,
  analysis_review: true,
  citation_navigation: true,
  workspace_assistance: true,
  evaluation_comparison: true,
  evaluation_review: true,
  ground_truth_navigation: true,
  ground_truth_annotation: true,
  document_navigation: true,
  document_upload: true,
  analysis_navigation: true,
  tsa_workspace: true,
  control_plane_overview: true,
} as const satisfies Record<PageCapability, true>;

/**
 * Every entity the API accepts, keyed by the API's own list so a key it adds
 * does not compile until it is listed here. Replicas of the previous release
 * reject a key they do not know (422), so, like a capability, hold a new key
 * back by hand for two releases ("Adding a page name" in
 * docs/architecture/folio-howto-design.md); until then its value can travel as
 * metadata, as a document's name does.
 */
const PAGE_CONTEXT_ENTITY_KEYS = {
  analysis_id: true,
  document_id: true,
  eval_run_id: true,
  eval_run_ids: true,
  project_id: true,
  project_name: true,
  revision_number: true,
  route_path: true,
  run_id: true,
  source_template_id: true,
  template_id: true,
  tenant_id: true,
  tenant_slug: true,
} as const satisfies Record<PageContextEntityKey, true>;

export type PageName = ApiPageContextOutput['page'];
export type PageCapability = NonNullable<ApiPageContextOutput['capabilities']>[number];
/** An entity a page context may name, such as `project_id`. */
export type PageContextEntityKey = keyof ApiPageContextEntitiesInput;
/** The entities a page names; a key the API does not accept is a type error. */
export type PageContextEntities = Readonly<{
  [Key in PageContextEntityKey]?: DefinedString<ApiPageContextEntitiesInput[Key]>;
}>;

export type PageContextMetadata = Readonly<Record<string, string>>;

export interface PageContextTransition {
  readonly kind: ApiPageContextTransitionOutput['kind'];
  readonly from_page: SendablePageName;
  readonly from_entities: PageContextEntities;
}

/** A page context the web may send: its page names are ones every API replica accepts. */
export interface PageContext {
  readonly page: SendablePageName;
  readonly page_id?: string;
  readonly entities: PageContextEntities;
  readonly capabilities: readonly PageCapability[];
  readonly context_transition?: PageContextTransition;
  readonly metadata?: PageContextMetadata;
  /**
   * The results on screen, sent beside the page context as the request's
   * `result_scope` rather than inside it, and never stored with the conversation.
   */
  readonly resultScope?: FolioResultScope;
}

function isPageName(value: string): value is PageName {
  return Object.hasOwn(PAGE_NAMES, value);
}

function isSendablePageName(name: PageName): name is SendablePageName {
  return PAGE_NAMES[name] === 'sendable';
}

function isPageCapability(value: string): value is PageCapability {
  return Object.hasOwn(PAGE_CAPABILITIES, value);
}

function isPageContextEntityKey(value: string): value is PageContextEntityKey {
  return Object.hasOwn(PAGE_CONTEXT_ENTITY_KEYS, value);
}

const ENTITY_KEYS: readonly PageContextEntityKey[] =
  Object.keys(PAGE_CONTEXT_ENTITY_KEYS).filter(isPageContextEntityKey);

/** A known page name the web may also send; `null` for one still awaiting release. */
function toSendablePageName(name: PageName | null | undefined): SendablePageName | null {
  return name != null && isSendablePageName(name) ? name : null;
}

type PageContextEntityEntry = [PageContextEntityKey, string];

function toPageContextEntities(entries: readonly PageContextEntityEntry[]): PageContextEntities {
  const entities: MutablePageContextEntities = {};
  for (const [key, value] of entries) {
    entities[key] = value;
  }
  return entities;
}

/**
 * A page context as its key spells it: the identity of a registration, without
 * the entities that change while a page loads. `createPageContextKey` writes it
 * and `parsePageContextKey` reads it back. A part that does not read back is
 * dropped rather than failing the whole context; an unknown page fails it.
 */
const pageContextKeySchema = z
  .object({
    page: z.enum(
      Object.keys(PAGE_NAMES).filter(
        (name): name is SendablePageName => isPageName(name) && isSendablePageName(name),
      ),
    ),
    page_id: z.string().min(1).optional().catch(undefined),
    entities: z.array(z.tuple([z.enum(ENTITY_KEYS), z.string()])).catch([]),
    capabilities: z.array(z.enum(Object.keys(PAGE_CAPABILITIES).filter(isPageCapability))).catch([]),
  })
  .transform(({ page, page_id: pageId, entities, capabilities }): PageContext => ({
    page,
    ...(pageId === undefined ? {} : { page_id: pageId }),
    entities: toPageContextEntities(entities),
    capabilities,
  }));

/**
 * The entities the API accepts, from a page's registration or a stored
 * context. Anything else is dropped rather than sent: a replica rejects a
 * context with a key it does not know.
 */
function normalizePageContextEntities(
  entities: StoredPageContextEntities | PageContextEntities | null | undefined,
): PageContextEntities {
  const normalized: MutablePageContextEntities = {};
  if (entities == null) {
    return normalized;
  }
  for (const key of ENTITY_KEYS) {
    const value = entities[key];
    if (value !== undefined) {
      normalized[key] = value;
    }
  }
  return normalized;
}

function normalizePageContextCapabilities(
  capabilities: ApiPageContextOutput['capabilities'] | readonly PageCapability[] | null | undefined,
): readonly PageCapability[] {
  return (capabilities ?? []).filter(
    (capability): capability is PageCapability => typeof capability === 'string',
  );
}

function normalizeTransition(
  transition: ApiPageContextTransitionOutput | null | undefined,
): PageContextTransition | undefined {
  const fromPage = toSendablePageName(transition?.from_page);
  if (transition == null || fromPage === null) {
    return undefined;
  }
  return {
    kind: transition.kind,
    from_page: fromPage,
    from_entities: normalizePageContextEntities(transition.from_entities),
  };
}

function buildFallbackEntities(
  pathname: string,
  routeParams: Readonly<Record<string, string | undefined>>,
  tenantId: string | null,
  tenantSlug: string | null,
  projectId: string | null,
): PageContextEntities {
  const resolvedProjectId = projectId ?? routeParams['projectId'] ?? null;
  return {
    ...(pathname.length === 0 ? {} : { route_path: pathname }),
    ...(tenantId === null ? {} : { tenant_id: tenantId }),
    ...(tenantSlug === null ? {} : { tenant_slug: tenantSlug }),
    ...(resolvedProjectId === null ? {} : { project_id: resolvedProjectId }),
    ...(routeParams['analysisId'] === undefined ? {} : { analysis_id: routeParams['analysisId'] }),
    ...(routeParams['documentId'] === undefined ? {} : { document_id: routeParams['documentId'] }),
    ...(routeParams['evalRunId'] === undefined ? {} : { eval_run_id: routeParams['evalRunId'] }),
    ...(routeParams['runId'] === undefined ? {} : { run_id: routeParams['runId'] }),
    ...(routeParams['templateId'] === undefined ? {} : { template_id: routeParams['templateId'] }),
  };
}

/** The page a route is registered as, before the page itself says anything richer. */
export interface RoutePage {
  readonly page: SendablePageName;
  readonly page_id: string;
  readonly capabilities: readonly PageCapability[];
}

/**
 * The context Sean works from until the page registers its own: the route's
 * registered page, or `unregistered-route` (404s, redirects), which hides Sean.
 */
export function createFallbackPageContext(
  pathname: string,
  routeParams: Readonly<Record<string, string | undefined>>,
  tenantId: string | null,
  tenantSlug: string | null,
  projectId: string | null,
  routePage: RoutePage | null,
): PageContext {
  const entities = buildFallbackEntities(pathname, routeParams, tenantId, tenantSlug, projectId);
  if (routePage === null) {
    return { page: 'unregistered-route', entities, capabilities: [] };
  }
  return {
    page: routePage.page,
    page_id: routePage.page_id,
    entities,
    capabilities: [...routePage.capabilities],
  };
}

export function clonePageContext(pageContext: PageContext): PageContext {
  const hasMetadata =
    pageContext.metadata !== undefined && Object.keys(pageContext.metadata).length > 0;
  return {
    page: pageContext.page,
    ...(pageContext.page_id === undefined ? {} : { page_id: pageContext.page_id }),
    entities: normalizePageContextEntities(pageContext.entities),
    capabilities: [...pageContext.capabilities],
    ...(pageContext.context_transition === undefined
      ? {}
      : {
          context_transition: {
            kind: pageContext.context_transition.kind,
            from_page: pageContext.context_transition.from_page,
            from_entities: normalizePageContextEntities(pageContext.context_transition.from_entities),
          },
        }),
    ...(hasMetadata ? { metadata: { ...pageContext.metadata } } : {}),
    ...(pageContext.resultScope === undefined ? {} : { resultScope: pageContext.resultScope }),
  };
}

const VOLATILE_ENTITY_KEYS: ReadonlySet<string> = new Set<PageContextEntityKey>([
  'project_name',
  'route_path',
]);

export function createPageContextKey(pageContext: PageContext): string {
  const stableEntries = ENTITY_KEYS.flatMap((key): PageContextEntityEntry[] => {
    const value = pageContext.entities[key];
    return value === undefined || VOLATILE_ENTITY_KEYS.has(key) ? [] : [[key, value]];
  }).sort(([left], [right]) => left.localeCompare(right));
  const serialized: z.input<typeof pageContextKeySchema> = {
    page: pageContext.page,
    ...(pageContext.page_id === undefined ? {} : { page_id: pageContext.page_id }),
    entities: stableEntries,
    capabilities: [...new Set(pageContext.capabilities)].sort((left, right) =>
      left.localeCompare(right),
    ),
  };

  return JSON.stringify(serialized);
}

export function parsePageContextKey(pageContextKey: string): PageContext {
  const fallback: PageContext = {
    page: 'unregistered-route',
    entities: {},
    capabilities: [],
  };

  try {
    const parsed = pageContextKeySchema.safeParse(JSON.parse(pageContextKey));
    return parsed.success ? parsed.data : fallback;
  } catch {
    return fallback;
  }
}

/**
 * A conversation's last page context as the API stored it, or `null` when it
 * names a page the web cannot send back yet (another client stored it).
 */
export function normalizeStoredPageContext(
  pageContext: ApiPageContextOutput | null | undefined,
): PageContext | null {
  const page = toSendablePageName(pageContext?.page);
  if (pageContext == null || page === null) {
    return null;
  }
  const contextTransition = normalizeTransition(pageContext.context_transition);
  const hasMetadata =
    pageContext.metadata !== undefined && Object.keys(pageContext.metadata).length > 0;
  const pageId = pageContext.page_id ?? undefined;
  return {
    page,
    ...(pageId === undefined ? {} : { page_id: pageId }),
    entities: normalizePageContextEntities(pageContext.entities),
    capabilities: normalizePageContextCapabilities(pageContext.capabilities),
    ...(contextTransition === undefined ? {} : { context_transition: contextTransition }),
    ...(hasMetadata ? { metadata: pageContext.metadata } : {}),
  };
}

export function toApiPageContext(pageContext: PageContext): ApiPageContextInput {
  const hasMetadata =
    pageContext.metadata !== undefined && Object.keys(pageContext.metadata).length > 0;
  return {
    page: pageContext.page,
    ...(pageContext.page_id === undefined ? {} : { page_id: pageContext.page_id }),
    entities: normalizePageContextEntities(pageContext.entities),
    capabilities: [...pageContext.capabilities],
    ...(pageContext.context_transition === undefined
      ? {}
      : {
          context_transition: {
            kind: pageContext.context_transition.kind,
            from_page: pageContext.context_transition.from_page,
            from_entities: normalizePageContextEntities(pageContext.context_transition.from_entities),
          },
        }),
    ...(hasMetadata ? { metadata: { ...pageContext.metadata } } : {}),
  };
}
